import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShoppingCartIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  CreditCardIcon,
  TruckIcon,
  ShieldCheckIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    getCartTotal,
    getCartItemsCount 
  } = useCart();
  
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);

  const applyCoupon = () => {
    // Sample coupon logic
    if (couponCode.toLowerCase() === 'welcome10') {
      setDiscount(10);
    } else if (couponCode.toLowerCase() === 'luxury20') {
      setDiscount(20);
    } else {
      alert('Geçersiz kupon kodu');
    }
  };

  const getItemTotal = (item) => {
    // Takım ürünleri için özel fiyat hesaplaması
    if (item.isSet) {
      return item.price * item.quantity; // price zaten final fiyat (indirimli)
    }
    return (item.salePrice || item.price) * item.quantity;
  };

  const getSubtotal = () => {
    return getCartTotal();
  };

  const getDiscountAmount = () => {
    return (getSubtotal() * discount) / 100;
  };

  const getShippingCost = () => {
    return getSubtotal() > 50000 ? 0 : 2500; // 50000 TL üzeri ücretsiz kargo
  };

  const getTotal = () => {
    return getSubtotal() - getDiscountAmount() + getShippingCost();
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      updateQuantity(itemId, newQuantity);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-6">
            <ShoppingCartIcon className="h-8 w-8 text-slate-600" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-luxury font-bold text-gray-900 mb-4">
            Alışveriş Sepeti
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Seçtiğiniz ürünleri gözden geçirin ve siparişinizi tamamlayın
          </p>
        </motion.div>

        {!cartItems || cartItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center py-16"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <ShoppingCartIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Sepetiniz boş
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Henüz sepetinize ürün eklemediniz. Hemen alışverişe başlayın!
            </p>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 bg-slate-600 text-white font-semibold rounded-lg hover:bg-slate-700 transition-colors duration-300"
            >
              Alışverişe Başla
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Sepetinizdeki Ürünler ({getCartItemsCount()})
                  </h2>
                  <button
                    onClick={clearCart}
                    className="text-red-500 hover:text-red-700 text-sm font-medium transition-colors duration-200"
                  >
                    Sepeti Boşalt
                  </button>
                </div>
                
                <div className="space-y-6">
                  {cartItems && cartItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center space-x-4 p-4 border border-gray-100 rounded-xl hover:shadow-md transition-shadow duration-300"
                    >
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {item.name}
                          </h3>
                          {item.isSet && (
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                              Takım
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-2">
                          Kategori: {item.category}
                        </p>

                        {/* Takım içeriği gösterimi */}
                        {item.isSet && item.setItems && (
                          <div className="mb-2 p-2 bg-gray-50 rounded-lg">
                            <p className="text-xs font-medium text-gray-700 mb-1">Takım İçeriği:</p>
                            <div className="space-y-1">
                              {item.setItems.map((setItem, idx) => (
                                <div key={idx} className="flex justify-between text-xs text-gray-600">
                                  <span>• {setItem.name}</span>
                                  <span>{setItem.quantity} adet</span>
                                </div>
                              ))}
                            </div>
                            {item.discount > 0 && (
                              <div className="mt-1 text-xs text-green-600 font-medium">
                                🎁 Takım indirimi: -₺{item.discount.toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          {item.isSet ? (
                            <div className="space-y-1">
                              {item.originalPrice && item.discount > 0 ? (
                                <>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-400 line-through">
                                      ₺{item.originalPrice.toLocaleString()}
                                    </span>
                                    <span className="text-lg font-bold text-emerald-600">
                                      ₺{item.price.toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="text-xs text-green-600">
                                    ₺{item.discount.toLocaleString()} tasarruf
                                  </div>
                                </>
                              ) : (
                                <span className="text-lg font-bold text-gray-900">
                                  ₺{item.price.toLocaleString()}
                                </span>
                              )}
                            </div>
                          ) : (
                            <>
                              {item.salePrice ? (
                                <>
                                  <span className="text-lg font-bold text-emerald-600">
                                    ₺{item.salePrice.toLocaleString()}
                                  </span>
                                  <span className="text-sm text-gray-400 line-through">
                                    ₺{item.price.toLocaleString()}
                                  </span>
                                </>
                              ) : (
                                <span className="text-lg font-bold text-gray-900">
                                  ₺{item.price.toLocaleString()}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                        >
                          <MinusIcon className="h-4 w-4 text-gray-600" />
                        </button>
                        <span className="w-8 text-center font-semibold text-gray-900">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                        >
                          <PlusIcon className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>

                      {/* Item Total */}
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          ₺{getItemTotal(item).toLocaleString()}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Ürünü Kaldır"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-lg p-6 sticky top-24"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Sipariş Özeti
                </h2>

                {/* Coupon Code */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kupon Kodu
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Kupon kodunuzu girin"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                    <button
                      onClick={applyCoupon}
                      className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-200"
                    >
                      Uygula
                    </button>
                  </div>
                  {discount > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      %{discount} indirim uygulandı!
                    </p>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Ara Toplam</span>
                    <span>₺{getSubtotal().toLocaleString()}</span>
                  </div>
                  
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>İndirim (%{discount})</span>
                      <span>-₺{getDiscountAmount().toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-gray-600">
                    <span>Kargo</span>
                    <span>
                      {getShippingCost() === 0 ? (
                        <span className="text-green-600 font-semibold">Ücretsiz</span>
                      ) : (
                        `₺${getShippingCost().toLocaleString()}`
                      )}
                    </span>
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold text-gray-900">
                      <span>Toplam</span>
                      <span>₺{getTotal().toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping Info */}
                {getShippingCost() > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg mb-6">
                    <div className="flex items-center space-x-2 text-blue-700">
                      <TruckIcon className="h-5 w-5" />
                      <span className="text-sm">
                        ₺{(50000 - getSubtotal()).toLocaleString()} daha alışveriş yapın, ücretsiz kargo kazanın!
                      </span>
                    </div>
                  </div>
                )}

                {/* Benefits */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <TruckIcon className="h-5 w-5 text-green-500" />
                    <span>Hızlı teslimat</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <ShieldCheckIcon className="h-5 w-5 text-green-500" />
                    <span>Güvenli ödeme</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <HeartIcon className="h-5 w-5 text-green-500" />
                    <span>30 gün iade garantisi</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <Link
                  to="/checkout"
                  className="w-full bg-slate-600 hover:bg-slate-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <CreditCardIcon className="h-5 w-5" />
                  <span>Siparişi Tamamla</span>
                </Link>

                {/* Continue Shopping */}
                <Link
                  to="/products"
                  className="w-full mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold transition-colors duration-300 text-center block"
                >
                  Alışverişe Devam Et
                </Link>
              </motion.div>
            </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default CartPage; 